/// Time zones for a club. The server takes any IANA name
/// (services/orgs/src/utils/timezone.ts); the panel offers a short list of the
/// zones our clubs are actually in, Kyiv first, then every zone the browser
/// knows — searchable by the Ukrainian city, the IANA id or the offset.

export const DEFAULT_ZONE = 'Europe/Kyiv'

/// CLDR (and so `Intl.supportedValuesOf`) still calls Kyiv by its Soviet-era
/// spelling. The panel never shows it, and a branch stored with the old name
/// is the same zone.
const ALIASES: Readonly<Record<string, string>> = { 'Europe/Kiev': DEFAULT_ZONE }

export const canonicalZone = (zone: string): string => ALIASES[zone] ?? zone

/// The clock a plan draft is tried on (specs/issued-passes.md §1b): its
/// club's, or — a network plan, valid at every club — the company's home
/// zone, the one services/passes stamps on such a plan (`homeTimeZone` from
/// services/orgs). Kyiv only when an older orgs does not say.
export const draftZone = (branchZone: string | undefined, homeZone: string | undefined): string =>
  canonicalZone(branchZone ?? homeZone ?? DEFAULT_ZONE)

/// The suggested zones, in the order offered, with their Ukrainian names.
export const SUGGESTED_ZONES: readonly (readonly [string, string])[] = [
  ['Europe/Kyiv', 'Київ'],
  ['Europe/Warsaw', 'Варшава'],
  ['Europe/Vilnius', 'Вільнюс'],
  ['Europe/Riga', 'Рига'],
  ['Europe/Tallinn', 'Таллінн'],
  ['Europe/Chisinau', 'Кишинів'],
  ['Europe/Bucharest', 'Бухарест'],
  ['Europe/Prague', 'Прага'],
  ['Europe/Berlin', 'Берлін'],
  ['Europe/Vienna', 'Відень'],
  ['Europe/Budapest', 'Будапешт'],
  ['Europe/Paris', 'Париж'],
  ['Europe/Madrid', 'Мадрид'],
  ['Europe/Rome', 'Рим'],
  ['Europe/Lisbon', 'Лісабон'],
  ['Europe/London', 'Лондон'],
  ['America/New_York', 'Нью-Йорк'],
  ['Asia/Dubai', 'Дубай'],
  ['UTC', 'UTC'],
]

const UK_NAMES = new Map(SUGGESTED_ZONES)

/// «Buenos Aires» from `America/Argentina/Buenos_Aires`, or the Ukrainian name
/// of a suggested zone.
export const zoneCity = (zone: string): string => {
  const canonical = canonicalZone(zone)
  return UK_NAMES.get(canonical) ?? (canonical.split('/').pop() ?? canonical).replaceAll('_', ' ')
}

/// «UTC+2», «UTC+5:30», «UTC» — the offset at `at` (daylight saving moves it).
export const zoneOffset = (zone: string, at: Date): string => {
  try {
    const name =
      new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortOffset' })
        .formatToParts(at)
        .find((part) => part.type === 'timeZoneName')?.value ?? ''
    const offset = name.replace(/^GMT/, '')
    return offset && offset !== '+0' && offset !== '-0' ? `UTC${offset}` : 'UTC'
  } catch {
    return ''
  }
}

export type ZoneOption = Readonly<{
  id: string
  city: string
  offset: string
  suggested: boolean
}>

/// Every zone to offer: the suggested ones first, then the rest of what the
/// runtime knows, A→Z, without duplicates or the old Kyiv spelling.
/// `known` is `Intl.supportedValuesOf('timeZone')` where it exists.
export const zoneOptions = (known: readonly string[], at: Date): readonly ZoneOption[] => {
  const suggested = SUGGESTED_ZONES.map(([id]) => id)
  const rest = [...new Set(known.map(canonicalZone))]
    .filter((id) => !suggested.includes(id))
    .sort((a, b) => a.localeCompare(b))
  return [...suggested, ...rest].map((id) => ({
    id,
    city: zoneCity(id),
    offset: zoneOffset(id, at),
    suggested: suggested.includes(id),
  }))
}

const fold = (value: string): string => value.toLocaleLowerCase('uk').replaceAll('_', ' ').trim()

/// Matches the Ukrainian name, the IANA id («warsaw», «europe/war») or the
/// offset («+2», «utc+5:30»). An empty query matches everything.
export const searchZones = (options: readonly ZoneOption[], query: string): readonly ZoneOption[] => {
  const q = fold(query)
  if (!q) return options
  return options.filter((option) =>
    [option.city, option.id, option.offset].some((field) => fold(field).includes(q)),
  )
}

/// What the browser knows; an empty list (old browsers) leaves only the
/// suggestions, which is still every zone a club of ours is in.
export const browserZones = (): readonly string[] => {
  try {
    return Intl.supportedValuesOf('timeZone')
  } catch {
    return []
  }
}
